"use client"

import { iconsMap } from "@/public/icons/iconsMap"
import styled from "@emotion/styled"
import { Box } from "@mui/material"
import Image from "next/image"
import { useInView } from "react-intersection-observer"
import {
  transitionDelayTime,
  useInViewConfig,
  useIsDesktop,
  useTransitionFadeInStyle,
  useTransitionUpStyles,
} from "ui"
import { isLottieFile } from "ui/common/bit/isLottieFile"
import LottiePlayer from "ui/common/bit/LottiePlayer"
import { mediaQueries } from "ui/theme/mediaQueries"
import { sizes } from "ui/theme/theme"
import { getUmbracoMediaUrl } from "utils"
import { sendBlockEvent } from "utils/gtm"
import CommunityTexts from "../common/bit/CommunityTexts"
import BlockWrapper from "../common/BlockWrapper"
import CommunityFooter from "./CommunityFooter"

const bwSizes = sizes.BlockWrapper

const StyledBlockWrapper = styled(BlockWrapper)`
  flex-flow: row wrap;
  align-items: center;
  justify-content: center;
  ${mediaQueries.tabletDown} {
    /* margin-top: 0; */
    padding-top: 30px;
  }
  ${mediaQueries.desktopM} {
    /* margin-top: ${bwSizes.desktopM.marginTop}; */
  }
  ${mediaQueries.desktopXl} {
    padding-inline-start: ${bwSizes.desktopXl.padding};
    padding-inline-end: ${bwSizes.desktopXl.padding};
  }
`

const StyledInnerContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  ${mediaQueries.desktopM} {
    flex-direction: row;
  }
  ${mediaQueries.tabletDown} {
    flex-direction: column;
  }
`

const ImagesWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "changeSides",
})<{ changeSides?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 40%;
  ${mediaQueries.mobile} {
    width: 100%;
  }
  ${mediaQueries.tablet} {
    width: 100%;
    order: ${(props) => (props.changeSides ? 1 : 2)};
  }
  ${mediaQueries.desktopM} {
    width: 40%;
    order: ${(props) => (props.changeSides ? 1 : 2)};
  }
  img {
    top: unset;
  }
`
const BGImageContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "changeSides",
})<{ changeSides?: boolean }>`
  display: flex;
  align-items: center;
  position: absolute;
  ${mediaQueries.mobile} {
    width: 100%;
    height: 330px;
    inset: auto 0 0 auto;
  }
  ${mediaQueries.tablet} {
    width: 100%;
    height: 330px;
    inset: auto 0 0 auto;
  }
  ${mediaQueries.desktopM} {
    inset: 0 auto auto 0px;
    width: ${(props) => (props.changeSides ? "1000px" : "750px")};
    height: ${(props) => (props.changeSides ? "1000px" : "515px")};
    left: ${(props) => (props.changeSides ? `-400px` : "unset")};
    transform: ${(props) => (props.changeSides ? "rotate(90deg)" : "unset")};
  }
  ${mediaQueries.desktopXl} {
    left: ${(props) =>
      props.changeSides ? `-${bwSizes.desktopM.padding}` : "unset"};
  }
`
const MainImageContainer = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "changeSides" && prop !== "noBackground",
})<{ changeSides?: boolean; noBackground?: boolean }>`
  position: relative;
  aspect-ratio: 15 / 16;
  margin-inline-start: ${(props) =>
    props.changeSides ? bwSizes.mobile.paddingLeft : "unset"};
  ${mediaQueries.mobile} {
    width: ${(props) => (props.noBackground ? "100%" : "90%")};
    margin: 0 auto;
    margin-bottom: ${(props) => (props.noBackground ? "-10%" : "-56px")};
  }
  ${mediaQueries.tablet} {
    width: ${(props) => (props.noBackground ? "100%" : "60%")};
    margin: 0 auto;
    margin-bottom: ${(props) => (props.noBackground ? "-35%" : "-56px")};
    margin-inline-end: ${(props) =>
      props.changeSides ? bwSizes.mobile.paddingLeft : "unset"};
  }

  ${mediaQueries.desktopM} {
    height: ${(props) => (props.noBackground ? "400px" : "100%")};
    width: unset;
    margin: unset;
    margin-top: ${(props) => (props.noBackground ? "unset" : "auto")};
    margin-bottom: ${(props) => (props.noBackground ? "unset" : "-40%")};
    margin-inline-end: ${(props) =>
      props.changeSides ? bwSizes.mobile.paddingLeft : "unset"};
  }
  ${mediaQueries.desktop} {
    margin-bottom: ${(props) => (props.noBackground ? "unset" : "-60px")};
  }
`

const StyledCommunityWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "changeSides",
})<{ changeSides?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 50%;
  z-index: 2;
  ${mediaQueries.mobile} {
    max-width: 100%;
  }
  ${mediaQueries.tablet} {
    max-width: 100%;
  }

  ${mediaQueries.desktopM} {
    margin-top: unset;
    max-width: 50%;
    order: ${(props) => (props.changeSides ? 2 : 1)};
  }
  ${mediaQueries.desktopXl} {
    margin-inline-start: unset;
    margin-inline-end: "unset";
  }
`

type CommunityProps = {
  data: any
  id?: string
}
const Community = ({ data, id }: CommunityProps) => {
  const {
    title,
    subtitle,
    text,
    button,
    buttonPrevious,
    additionalButton,
    desktopImage,
    media,
    image,
    changeSides,
    noBackground,
    phoneList,
    bottomTitle,
    className,
    readMoreCheckbox,
  } = data

  const isDesktop = useIsDesktop()
  const { ref, inView } = useInView(useInViewConfig)
  const ditransitiveStyles = useTransitionUpStyles(inView, transitionDelayTime)
  const BgImageTransition = useTransitionFadeInStyle(
    inView,
    transitionDelayTime,
  )
  const ArrowIcon = iconsMap.left_red_arrow
  const currMainImage = desktopImage || image || media

  // GTM constants for this block
  const GTM_BLOCK_ID = "9"
  const GTM_BLOCK_TITLE = "הכשרה למען הקהילה"
  const GTM_COMPONENT_NAME = "למען הקהילה"

  // Handler for main CTA/button click
  const handleButtonClick = (buttonText?: string) => {
    sendBlockEvent({
      blockId: id || GTM_BLOCK_ID,
      componentName: GTM_COMPONENT_NAME,
      title: title?.value || GTM_BLOCK_TITLE,
      buttonName: buttonText,
      productName: data?.formDefaultInsuranceType,
    })
  }

  // Prepare the CommunityTexts data object
  const communityTextsData = {
    title,
    subtitle,
    text,
    hasExpandButton: !!readMoreCheckbox?.value,
    button,
    buttonPrevious,
    additionalButton,
    ArrowIcon,
  }

  return (
    <StyledBlockWrapper id={id} className={className}>
      <StyledInnerContainer>
        <StyledCommunityWrapper changeSides={changeSides?.value}>
          <CommunityTexts
            data={communityTextsData}
            onButtonClick={() => {
              handleButtonClick(
                button?.text ||
                  button?.name ||
                  button?.links?.[0]?.name ||
                  "CTA",
              )
              communityTextsData.button?.links?.[0]?.onClick?.()
            }}
            onAdditionalButtonClick={(e: any) => {
              handleButtonClick(
                additionalButton?.links?.[0]?.name ||
                  additionalButton?.links?.[0]?.text ||
                  "Additional CTA",
              )
              additionalButton?.links?.[0]?.onClick?.(e)
            }}
          />
        </StyledCommunityWrapper>
        <ImagesWrapper changeSides={changeSides?.value}>
          {noBackground?.value === false && (
            <BGImageContainer
              changeSides={changeSides?.value}
              ref={ref}
              style={{ ...BgImageTransition }}
            >
              {isDesktop ? (
                <LottiePlayer
                  lottieUrl="/animations/Gal_4.json"
                  playOnce={true}
                  autoplay={true}
                  loop={false}
                  ariaLabel="community Hachshara animation"
                  height="auto"
                />
              ) : (
                <LottiePlayer
                  lottieUrl="/animations/Gal_4.json"
                  playOnce={true}
                  autoplay={true}
                  loop={false}
                  ariaLabel="community Hachshara animation"
                  height="auto"
                  style={{ transform: "rotate(180deg) scaleX(-1)" }}
                />
              )}
            </BGImageContainer>
          )}
          {currMainImage?.mediaItems && (
            <MainImageContainer
              changeSides={changeSides?.value}
              noBackground={noBackground?.value}
              ref={ref}
              style={{ ...ditransitiveStyles }}
            >
              {isLottieFile(
                getUmbracoMediaUrl(currMainImage.mediaItems[0]?.url),
              ) ? (
                <LottiePlayer
                  lottieUrl={getUmbracoMediaUrl(
                    currMainImage.mediaItems[0].url,
                  )}
                  playOnce={true}
                  autoplay={true}
                  loop={false}
                  ariaLabel={
                    currMainImage.mediaItems[0]?.altText ||
                    "community Hachshara animation"
                  }
                />
              ) : (
                <Image
                  src={getUmbracoMediaUrl(currMainImage.mediaItems[0]?.url)}
                  alt={
                    currMainImage?.mediaItems[0]?.altText ||
                    "community Hachshara image"
                  }
                  fill
                  style={{
                    objectFit: "contain",
                    objectPosition:
                      noBackground?.value === true || !!phoneList
                        ? "center"
                        : "bottom",
                  }}
                  sizes="100vw"
                />
              )}
            </MainImageContainer>
          )}
        </ImagesWrapper>
      </StyledInnerContainer>
      <CommunityFooter data={{ phoneList, bottomTitle }} />
    </StyledBlockWrapper>
  )
}

export default Community
